import { useMemo, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button } from "ui/button";
import { DialogActions, DialogBackButton } from "ui/dialog";

import { useCampaignFromTemplateContext } from "components/modals/create-campaign-from-template-modal/context/campaign-from-template-context";
import { TouchpointTemplateItem } from "components/modals/create-campaign-from-template-modal/components/touchpoint-template-item";

import { MondayTouchpointTemplateDTO } from "generated/schemas";

const TEMPLATE_LIST_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 2 };

const TouchpointSelectionStep = () => {
  const { campaignTemplates, campaignObjectives, invalidTemplateIds } =
    useCampaignFromTemplateContext();
  const { watch } = useFormContext();

  const selectedCampaignObjective = watch("campaignObjective");
  const watchedTemplateValues = watch("campaignTemplates");

  const campaignObjectiveDescription = useMemo(() => {
    const objective = campaignObjectives.find((obj) => obj.name === selectedCampaignObjective);
    return objective?.description || selectedCampaignObjective;
  }, [selectedCampaignObjective, campaignObjectives]);

  const selectedTouchpointsTemplates = useMemo(() => {
    const selectedTemplateValues = watchedTemplateValues || [];
    return selectedTemplateValues
      .map((templateId: string) => {
        return campaignTemplates.find((template) => template.id === templateId)
          ?.touchpointTemplates;
      })
      .filter((templates: MondayTouchpointTemplateDTO[] | undefined) => templates !== undefined)
      .flat() as MondayTouchpointTemplateDTO[];
  }, [watchedTemplateValues, campaignTemplates]);

  const renderTemplates = useCallback(() => {
    return selectedTouchpointsTemplates.map((template) => (
      <TouchpointTemplateItem
        key={template.id}
        template={template}
        invalidTemplateIds={invalidTemplateIds}
        campaignObjectiveDescription={campaignObjectiveDescription}
      />
    ));
  }, [selectedTouchpointsTemplates, invalidTemplateIds, campaignObjectiveDescription]);

  return (
    <div className="flex flex-col">
      <p className="mb-4">Please check campaign content and proceed.</p>
      <p className="mb-4">Selected items</p>
      <Box sx={TEMPLATE_LIST_SX}>{renderTemplates()}</Box>
    </div>
  );
};

export const TouchpointSelectionStepActions = () => {
  const { next, back } = useCampaignFromTemplateContext();
  const { watch } = useFormContext();

  const templates = watch("templates");
  const hasSelectedTemplates = Object.values(templates ?? {}).some((value) => value === true);

  return (
    <DialogActions
      type="steps"
      back={<DialogBackButton onClick={back}>Back to templates</DialogBackButton>}
      primary={
        <Button size="large" onClick={next} disabled={!hasSelectedTemplates}>
          Continue with details
        </Button>
      }
    />
  );
};

export default TouchpointSelectionStep;
